import { useEffect, useRef } from 'react'

import { usePrevious } from './usePrevious'

/**
 * Re-requests the counters after every refetch of the list and returns the
 * counterpart of `reload` for the profile owner: a reload requested through
 * `handleReloadTabs` skips `onReloadCounters` once, because the owner requests
 * those counters itself (see `reloadRegistryTabsAndCounters`). The skip stays armed
 * until the next refetch ends: `useTableReloadAfterMassAction` may refetch only
 * after the filter values have been reloaded.
 *
 * @param {Object} params
 * @param {boolean} params.isRefetching
 * @param {Function} [params.reload]
 * @param {Function} [params.onReloadCounters] the profile tab counters
 * @param {Function} [params.onReloadOwnCounters] counters the tab itself owns
 *   (its sub-tab totals): the profile owner cannot reach them, so they are
 *   re-requested after every refetch, owner-driven ones included
 * @returns {{ handleReloadWithoutCounters: Function }}
 */
export function useReloadCountersAfterRefetch({
  isRefetching,
  reload,
  onReloadCounters,
  onReloadOwnCounters,
}) {
  const previousIsRefetching = usePrevious(isRefetching)
  const skipCountersRef = useRef(false)

  useEffect(() => {
    if (previousIsRefetching && !isRefetching) {
      onReloadOwnCounters?.()

      if (skipCountersRef.current) {
        skipCountersRef.current = false
      } else {
        onReloadCounters?.()
      }
    }
  }, [isRefetching, previousIsRefetching])

  async function handleReloadWithoutCounters(params) {
    skipCountersRef.current = true

    return reload?.(params)
  }

  return { handleReloadWithoutCounters }
}
